import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/App";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useTheme } from "@/hooks/useTheme";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sparkles,
  Database,
  Menu,
  LogOut,
  Radar,
  DollarSign,
  Trash2,
  BookOpen,
  CreditCard,
  ShieldCheck,
  Monitor,
  GitCompareArrows,
  Sun,
  Moon,
  MonitorSmartphone,
  HeartPulse,
  Rocket,
} from "lucide-react";

const navSections = [
  {
    label: "OVERVIEW",
    items: [
      { path: "/", label: "Insights", icon: Sparkles },
    ],
  },
  {
    label: "EXPLORE & ANALYZE",
    items: [
      { path: "/resources", label: "Resources", icon: Database },
      { path: "/costs", label: "Cost Intelligence", icon: DollarSign },
      { path: "/changes", label: "Drift & Changes", icon: GitCompareArrows },
      { path: "/hygiene", label: "Cloud Hygiene", icon: HeartPulse },
    ],
  },
  {
    label: "ACTIONS",
    items: [
      { path: "/cleanup", label: "Cleanup Wizard", icon: Trash2 },
      { path: "/deployments", label: "Deployments", icon: Rocket },
    ],
  },
  {
    label: "SETTINGS",
    items: [
      { path: "/billing", label: "Billing", icon: CreditCard },
      { path: "/system", label: "System", icon: Monitor },
      { path: "/docs", label: "Documentation", icon: BookOpen },
      { path: "/admin", label: "Admin", icon: ShieldCheck, admin: true },
    ],
  },
];

const SidebarContent = ({ onClose }) => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  const cycleTheme = () => {
    const order = ["system", "light", "dark"];
    const next = order[(order.indexOf(theme) + 1) % order.length];
    setTheme(next);
  };

  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : MonitorSmartphone;
  const themeLabel = theme === "dark" ? "Dark" : theme === "light" ? "Light" : "Auto";

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--n-border)]">
        <div className="w-8 h-8 rounded-[8px] bg-[var(--hig-accent)] flex items-center justify-center">
          <Radar className="w-4 h-4 text-white" />
        </div>
        <div>
          <h1 className="text-[15px] font-bold tracking-tight font-heading">Niyant</h1>
          <p className="text-[9px] uppercase tracking-[0.12em] text-[var(--n-text-muted)]">SRE · DevOps · CloudOps</p>
        </div>
      </div>

      {/* Nav */}
      <ScrollArea className="flex-1 py-2">
        {navSections.map((section) => {
          const items = section.items.filter((i) => !i.admin || user?.role === "admin");
          if (items.length === 0) return null;
          return (
            <div key={section.label} className="px-3 mb-0.5">
              <p className="text-[9px] font-semibold uppercase tracking-[0.1em] text-[var(--n-text-muted)] px-2.5 pt-4 pb-1">{section.label}</p>
              <nav className="space-y-px">
                {items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-2.5 py-[7px] rounded-[8px] text-[13px] hig-nav-link ${
                        isActive
                          ? "bg-[var(--hig-accent-subtle)] text-[var(--hig-accent)] font-medium"
                          : "text-[var(--n-text-secondary)] hover:bg-[var(--n-hover)]"
                      }`
                    }
                    end={item.path === "/"}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <item.icon className="w-[16px] h-[16px]" />
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          );
        })}
      </ScrollArea>

      {/* User */}
      <div className="px-3 py-3 border-t border-[var(--n-border)]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-[var(--hig-accent-subtle)] flex items-center justify-center text-[var(--hig-accent)] text-[11px] font-semibold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-medium truncate">{user?.name}</p>
            <p className="text-[10px] text-[var(--n-text-muted)] truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={cycleTheme}
            className="h-7 w-7 text-[var(--n-text-muted)] hover:text-foreground"
            data-tooltip={`Theme: ${themeLabel}`}
            data-testid="theme-toggle"
          >
            <ThemeIcon className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="h-7 w-7 text-[var(--n-text-muted)] hover:text-[var(--hig-danger)]"
            data-tooltip="Sign out"
            data-testid="logout-button"
          >
            <LogOut className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  useAnalytics();

  return (
    <div className="flex h-screen bg-background" data-testid="app-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[240px] flex-col border-r border-[var(--n-border)] bg-[var(--n-surface)]">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="w-[280px] p-0 bg-[var(--n-surface)] border-[var(--n-border)]"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarContent onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-[var(--n-border)] bg-[var(--n-surface)]">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            className="h-8 w-8"
            data-tooltip="Menu"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Radar className="w-4 h-4 text-[var(--hig-accent)]" />
            <span className="text-[15px] font-bold font-heading">Niyant</span>
          </div>
        </header>

        <ScrollArea className="flex-1">
          <div className="p-4 lg:p-8 max-w-[1400px] animate-page-enter">{children}</div>
        </ScrollArea>
      </main>
    </div>
  );
}
